import { fetchJSON as fetchAPI } from './client';

export type EngineeringPrinciple = {
  key: string;
  name: string;
  description: string;
  priority?: string;
};

export type CodingStandard = {
  key: string;
  domain: string;
  name: string;
  rules: string[];
  mandatory: boolean;
};

export type AIDevRule = {
  key: string;
  name: string;
  description: string;
  required: boolean;
};

export type QualityGate = {
  key: string;
  name: string;
  tool?: string;
  threshold?: string;
  blocking: boolean;
};

export type PerformanceBudget = {
  key: string;
  surface: string;
  max_value: string;
  unit: string;
  description?: string;
};

export type DatabaseRule = {
  key: string;
  name: string;
  description: string;
  mandatory: boolean;
};

export type APIStandard = {
  key: string;
  name: string;
  description: string;
  example?: string;
};

export type FrontendStandard = {
  key: string;
  name: string;
  category: string;
  description: string;
};

export type ObservabilityRequirement = {
  key: string;
  name: string;
  signal_type: string;
  mandatory: boolean;
};

export type SecurityRequirement = {
  key: string;
  name: string;
  standard?: string;
  mandatory: boolean;
};

export type TestingLevel = {
  key: string;
  name: string;
  scope: string;
  coverage?: string;
  tools?: string[];
};

export type DefinitionOfDoneItem = {
  key: string;
  name: string;
  category: string;
  required: boolean;
};

export type RepositoryStructureRule = {
  key: string;
  path: string;
  purpose: string;
  mandatory: boolean;
};

export type CICDRequirement = {
  key: string;
  name: string;
  stage: string;
  description: string;
  blocking: boolean;
};

export type ReviewChecklistItem = {
  key: string;
  item: string;
  category: string;
  required: boolean;
};

export type DeveloperPlaybookEntry = {
  key: string;
  title: string;
  phase: string;
  steps: string[];
};

export type EEBDashboard = {
  bible_id: string;
  tenant_id: string;
  version: string;
  applies_to: string[];
  engineering_principles: number;
  coding_standards: number;
  quality_gates: number;
  performance_budgets: number;
  security_requirements: number;
  testing_levels: number;
  definition_of_done_items: number;
  cicd_requirements: number;
  generated_at: string;
};

export type FeatureCompletionValidation = {
  feature_key: string;
  valid: boolean;
  production_code: boolean;
  automated_tests: boolean;
  documentation: boolean;
  observability: boolean;
  security_validated: boolean;
  performance_validated: boolean;
  gaps?: string[];
  evaluated_at: string;
};

export type EEBibleStatus = {
  bible_id: string;
  version: string;
  applies_to: string[];
  engineering_principles: number;
  coding_standards: number;
  quality_gates: number;
  performance_budgets: number;
  security_requirements: number;
  testing_levels: number;
  definition_of_done_items: number;
  cicd_requirements: number;
  engineering_constitution: boolean;
  ten_year_vision: boolean;
  status: string;
};

const GOVERNANCE_BASE = import.meta.env.VITE_GOVERNANCE_API ?? '/api/v1/governance';

function fetchGovernance<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${GOVERNANCE_BASE}${path}`, init);
}

export const engineeringExecutionBibleApi = {
  dashboard: () => fetchGovernance<EEBDashboard>('/eeb/dashboard'),
  principles: () => fetchGovernance<{ principles: EngineeringPrinciple[] }>('/eeb/principles').then((r) => r.principles),
  codingStandards: () => fetchGovernance<{ standards: CodingStandard[] }>('/eeb/coding-standards').then((r) => r.standards),
  aiDevelopment: () => fetchGovernance<{ rules: AIDevRule[] }>('/eeb/ai-development').then((r) => r.rules),
  qualityGates: () => fetchGovernance<{ gates: QualityGate[] }>('/eeb/quality-gates').then((r) => r.gates),
  performanceBudgets: () => fetchGovernance<{ budgets: PerformanceBudget[] }>('/eeb/performance-budgets').then((r) => r.budgets),
  databaseStandards: () => fetchGovernance<{ rules: DatabaseRule[] }>('/eeb/database-standards').then((r) => r.rules),
  apiStandards: () => fetchGovernance<{ standards: APIStandard[] }>('/eeb/api-standards').then((r) => r.standards),
  frontendStandards: () => fetchGovernance<{ standards: FrontendStandard[] }>('/eeb/frontend-standards').then((r) => r.standards),
  observability: () => fetchGovernance<{ requirements: ObservabilityRequirement[] }>('/eeb/observability').then((r) => r.requirements),
  securityStandards: () => fetchGovernance<{ requirements: SecurityRequirement[] }>('/eeb/security-standards').then((r) => r.requirements),
  testingStrategy: () => fetchGovernance<{ levels: TestingLevel[] }>('/eeb/testing-strategy').then((r) => r.levels),
  definitionOfDone: () => fetchGovernance<{ items: DefinitionOfDoneItem[] }>('/eeb/definition-of-done').then((r) => r.items),
  repositoryStructure: () => fetchGovernance<{ structure: RepositoryStructureRule[] }>('/eeb/repository-structure').then((r) => r.structure),
  cicdRequirements: () => fetchGovernance<{ requirements: CICDRequirement[] }>('/eeb/cicd-requirements').then((r) => r.requirements),
  reviewChecklist: () => fetchGovernance<{ checklist: ReviewChecklistItem[] }>('/eeb/review-checklist').then((r) => r.checklist),
  developerPlaybook: () => fetchGovernance<{ playbook: DeveloperPlaybookEntry[] }>('/eeb/developer-playbook').then((r) => r.playbook),
  validateFeature: (payload: { feature_key: string; attrs: Record<string, boolean> }) =>
    fetchGovernance<FeatureCompletionValidation>('/eeb/validate-feature', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  status: () => fetchGovernance<EEBibleStatus>('/eeb/status'),
};
